import { Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NumberRangeWithYearPeriod,
  SwitchWithNumberRangeYearPeriodFilter,
} from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import { YEAR_DROPDOWN_OPTIONS } from '../../Inputs/DateRangeWithDropdown';
import InputSwitch, { SwitchSize } from '../../Inputs/Switch/Switch';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';
import { allowedYearRange } from '../../Inputs/YearRange/YearRange';
import NumberRangePickerWithYearPeriodFilterInput from '../NumberRangePickerWithYearPeriodFilterInput';

interface Props {
  root?: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  onChange: (value: SwitchWithNumberRangeYearPeriodFilter) => void;
  defaultAllowedYearRange?: typeof allowedYearRange;
  defaultValue?: SwitchWithNumberRangeYearPeriodFilter;
  active: boolean;
}

const SwitchWithNumberRangeFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    root,
    active,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    defaultAllowedYearRange = allowedYearRange,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: !unknownSwitchEnabled ? unknownSwitchEnabled : false,
          isEnabled: false,
          value: {
            criteria: YEAR_DROPDOWN_OPTIONS[1].value as NumberRangeWithYearPeriod.CriteriaEnum,
            years: defaultAllowedYearRange,
            values: {
              from: undefined,
              fromInclusive: false,
              to: undefined,
              toInclusive: false,
            },
          },
        }
      : {
          isEnabled: false,
          value: {
            criteria: YEAR_DROPDOWN_OPTIONS[1].value as NumberRangeWithYearPeriod.CriteriaEnum,
            years: defaultAllowedYearRange,
            values: {
              from: undefined,
              fromInclusive: false,
              to: undefined,
              toInclusive: false,
            },
          },
        },
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<SwitchWithNumberRangeYearPeriodFilter>(defaultValue);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    if (state.isEnabled) {
      rs.push({ label: 'Excluded:', value: 'Negative results' });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}
            <Grid container direction="row">
              <Grid item className={classes.gridText}>
                <Typography className={classes.typography}>
                  {t('companies:filterInputs.excludeNegativeResults')}
                </Typography>
              </Grid>
              <Grid item>
                <InputSwitch
                  fieldName=""
                  onChange={(value) => {
                    setState({ ...state, isEnabled: value });
                  }}
                  checked={state.isEnabled}
                  size={SwitchSize.small}
                />
              </Grid>
            </Grid>
          </>
        ) : null}
        <NumberRangePickerWithYearPeriodFilterInput
          active={active}
          additionalReadableStatement={rs}
          defaultAllowedYearRange={defaultAllowedYearRange}
          defaultValue={state as any}
          onChange={(value) => {
            setState({ ...state, value: value.value });
          }}
        />
      </>
    );
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  gridText: {
    width: 'calc(100% - 48px)',
    display: 'flex',
    alignItems: 'center',
  },
  typography: {
    fontSize: theme.typography.fontSize - 2,
  },
}));

export default SwitchWithNumberRangeFilterInput;
